import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Users, 
  MessageSquare, 
  Target, 
  Search, 
  X, 
  ArrowUpDown, 
  Filter, 
  ChevronDown, 
  ChevronRight, 
  Maximize2, 
  Minimize2,
  Play,
  Radio,
  Zap,
  Eye
} from 'lucide-react';
import { VariableInfo, ReasoningEffort, FewShotExample, TargetVariable } from '../types/api';
import { apiService } from '../services/api';
import { cn } from '../lib/utils';
import ModelSelector from './ModelSelector';
import FewShotManager from './FewShotManager';

interface ParameterControlsProps {
  sampleSize: number;
  setSampleSize: (size: number) => void;
  selectedVariables: string[];
  setSelectedVariables: (variables: string[]) => void;
  customPrompt: string;
  setCustomPrompt: (prompt: string) => void;
  onRunSimulation: () => void;
  isLoading: boolean;
  reasoningEffort: ReasoningEffort;
  setReasoningEffort: (effort: ReasoningEffort) => void;
  enableChainOfThought: boolean;
  setEnableChainOfThought: (enabled: boolean) => void;
  fewShotExamples: FewShotExample[];
  setFewShotExamples: (examples: FewShotExample[]) => void;
  enableStreaming: boolean;
  setEnableStreaming: (enabled: boolean) => void;
  targetVariable: string;
  setTargetVariable: (variable: string) => void;
  maxConcurrency: number;
  setMaxConcurrency: (concurrency: number) => void;
}

const ParameterControls: React.FC<ParameterControlsProps> = ({
  sampleSize,
  setSampleSize,
  selectedVariables,
  setSelectedVariables,
  customPrompt,
  setCustomPrompt,
  onRunSimulation,
  isLoading,
  reasoningEffort,
  setReasoningEffort,
  enableChainOfThought,
  setEnableChainOfThought,
  fewShotExamples,
  setFewShotExamples,
  enableStreaming,
  setEnableStreaming,
  targetVariable,
  setTargetVariable,
  maxConcurrency,
  setMaxConcurrency,
}) => {
  const [variables, setVariables] = useState<VariableInfo[]>([]);
  const [targetVariables, setTargetVariables] = useState<TargetVariable[]>([]);
  const [loadingVariables, setLoadingVariables] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['demographics', 'political_engagement', 'political_trust']));
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'category' | 'importance'>('category');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'ai' | 'advanced'>('basic');
  const [targetVarSearch, setTargetVarSearch] = useState('');
  const [targetVarCategory, setTargetVarCategory] = useState<string>('all');
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  useEffect(() => {
    loadVariables();
    loadTargetVariables();
  }, []);

  // Set default selected variables when variables are loaded (only once)
  useEffect(() => {
    if (variables.length > 0 && selectedVariables.length === 0) {
      const recommended = variables
        .filter(v => getImportanceScore(v) >= 10)
        .map(v => v.name);
      if (recommended.length > 0) {
        setSelectedVariables(recommended);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables]);

  const loadTargetVariables = async () => {
    try {
      const { target_variables } = await apiService.getTargetVariables();
      setTargetVariables(target_variables);
    } catch (error) {
      console.error('Error loading target variables:', error);
    }
  };

  const loadVariables = async () => {
    try {
      const vars = await apiService.getVariables();
      setVariables(vars);
    } catch (error) {
      console.error('Error loading variables:', error);
    } finally {
      setLoadingVariables(false);
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleVariable = (variableName: string) => {
    if (selectedVariables.includes(variableName)) {
      setSelectedVariables(selectedVariables.filter(v => v !== variableName));
    } else {
      setSelectedVariables([...selectedVariables, variableName]);
    }
  };

  const selectAllInCategory = (category: string) => {
    const categoryVars = variables.filter(v => v.category === category).map(v => v.name);
    const newSelected = [...selectedVariables];
    categoryVars.forEach(varName => {
      if (!newSelected.includes(varName)) {
        newSelected.push(varName);
      }
    });
    setSelectedVariables(newSelected);
  };

  const deselectAllInCategory = (category: string) => {
    const categoryVars = variables.filter(v => v.category === category).map(v => v.name);
    setSelectedVariables(selectedVariables.filter(v => !categoryVars.includes(v)));
  };

  // Generate default prompt based on target variable
  const getDefaultPrompt = React.useMemo(() => {
    const targetVar = targetVariables.find(tv => tv.name === targetVariable);
    if (targetVar && targetVar.label) {
      // Convert label to question format
      let question = targetVar.label;
      
      // Handle common patterns
      if (question.toLowerCase().includes('trust in') || question.toLowerCase().includes('fiducia')) {
        question = question
          .replace(/^Trust in /i, '')
          .replace(/^Fiducia in /i, '')
          .replace(/^Fiducia nei /i, '')
          .replace(/^Fiducia nelle /i, '');
        question = `Lei ha fiducia ${question.toLowerCase().startsWith('in') ? '' : 'in '}${question.toLowerCase()}?`;
      } else {
        // Generic question format
        question = `Lei ha fiducia in ${question.toLowerCase()}?`;
      }
      
      return `${question}

Per favore risponda con una sola parola alla fine: si o no.
La parola finale della tua risposta deve essere unicamente 'si' o 'no'.
Non è consentita una risposta neutra o indifferente.`;
    }
    
    // Fallback to default
    return `Lei ha fiducia nei partiti politici?

Per favore risponda con una sola parola alla fine: si o no.
La parola finale della tua risposta deve essere unicamente 'si' o 'no'.
Non è consentita una risposta neutra o indifferente.`;
  }, [targetVariable, targetVariables]);

  const defaultPrompt = getDefaultPrompt;

  // Normalize Italian text for better matching (remove accents, handle case)
  const normalizeItalianText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD') // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ç]/g, 'c');
  };

  // Improved fuzzy search helper - very tolerant, handles Italian text well
  const fuzzyMatch = (searchTerm: string, ...texts: (string | undefined | null)[]): boolean => {
    if (!searchTerm.trim()) return true;
    
    const normalizedSearch = normalizeItalianText(searchTerm.trim());
    const searchWords = normalizedSearch.split(/\s+/).filter(w => w.length > 2); // Ignore very short words
    
    // If no meaningful search words, match everything
    if (searchWords.length === 0) return true;
    
    // Combine all text fields into one searchable string and normalize
    const searchableText = normalizeItalianText(
      texts
        .filter(t => t != null)
        .map(t => String(t))
        .join(' ')
    );
    
    // Very lenient matching: check if all search words appear somewhere
    // Words can appear in any order, anywhere in the text
    return searchWords.every(word => {
      // Exact substring match (most common case)
      if (searchableText.includes(word)) return true;
      
      // Partial word match - check if significant portion of word appears
      if (word.length >= 4) {
        const prefix = word.substring(0, Math.ceil(word.length * 0.7)); // 70% of word
        if (searchableText.includes(prefix)) return true;
      }
      
      // Character-based fuzzy match - characters appear in order but allow gaps
      let textIndex = 0;
      let matchedChars = 0;
      for (let i = 0; i < word.length; i++) {
        const char = word[i];
        const foundIndex = searchableText.indexOf(char, textIndex);
        if (foundIndex !== -1) {
          matchedChars++;
          textIndex = foundIndex + 1;
        }
      }
      // Match if at least 70% of characters found in order
      return matchedChars >= Math.ceil(word.length * 0.7);
    });
  };

  const getImportanceScore = (variable: VariableInfo): number => {
    let score = 0;
    
    const categoryScores: Record<string, number> = {
      'demographics': 8,
      'political_engagement': 9,
      'government_evaluation': 7,
      'european_attitudes': 6,
      'values': 5,
      'political_trust': 8,
      'justice_security_trust': 6,
      'economic_trust': 6,
      'media_trust': 5,
      'social_trust': 5,
      'institutional_trust': 4,
      'economic_attitudes': 6,
      'social_cultural': 5,
      'media_information': 4,
      'environment': 5,
      'immigration_diversity': 6,
      'technology': 4,
      'other': 3
    };
    score += categoryScores[variable.category || 'other'] || 3;
    
    const keyVariables = [
      'P3', 'E1', 'INTVOTO', 'E15', 'Collocazione',
      'I1', 'I2',
      'UE1', 'UE2',
      'SESSO', 'ETA', 'TITREC', 'LAVOROPOND', 'AREAGEO'
    ];
    
    if (keyVariables.some(key => variable.name.includes(key))) {
      score += 3;
    }
    
    if (selectedVariables.includes(variable.name)) {
      score += 2;
    }
    
    return score;
  };

  let processedVariables = variables.filter(variable => {
    if (filterCategory !== 'all' && variable.category !== filterCategory) {
      return false;
    }
    
    if (searchTerm) {
      // Fuzzy search across name, label, category, description, and value_labels
      const valueLabelsText = variable.value_labels 
        ? Object.values(variable.value_labels).join(' ')
        : '';
      
      return fuzzyMatch(
        searchTerm,
        variable.name,
        variable.label,
        variable.category,
        (variable as any).description, // Some variables might have description
        valueLabelsText // Search in value labels too
      );
    }
    
    return true;
  });

  processedVariables.sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'importance':
        return getImportanceScore(b) - getImportanceScore(a);
      case 'category':
      default:
        const catA = a.category || 'other';
        const catB = b.category || 'other';
        if (catA !== catB) {
          return catA.localeCompare(catB);
        }
        return getImportanceScore(b) - getImportanceScore(a);
    }
  });

  const variablesByCategory = processedVariables.reduce((acc, variable) => {
    const category = variable.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(variable);
    return acc;
  }, {} as Record<string, VariableInfo[]>);

  const availableCategories = Array.from(new Set(variables.map(v => v.category || 'other')));

  const categoryLabels: Record<string, string> = {
    demographics: 'Demographics',
    political_engagement: 'Political Engagement',
    government_evaluation: 'Government Evaluation',
    european_attitudes: 'European Attitudes',
    values: 'Values & Social Attitudes',
    political_trust: 'Political Trust',
    justice_security_trust: 'Justice & Security Trust',
    economic_trust: 'Economic Trust',
    media_trust: 'Media Trust',
    social_trust: 'Social Trust',
    institutional_trust: 'Other Institutional Trust',
    economic_attitudes: 'Economic Attitudes',
    social_cultural: 'Social & Cultural Issues',
    media_information: 'Media & Information',
    environment: 'Environmental Issues',
    immigration_diversity: 'Immigration & Diversity',
    technology: 'Technology & Innovation',
    other: 'Other'
  };

  return (
    <div className={cn(
      "rounded-lg border bg-card transition-all duration-200",
      isExpanded ? "fixed inset-4 z-40 p-6 overflow-y-auto" : "p-5"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <Settings className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-base font-semibold">Parameters</h2>
            <p className="text-xs text-muted-foreground">Configure simulation</p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2 hover:bg-muted rounded-md transition-colors"
          title={isExpanded ? "Minimize" : "Expand"}
        >
          {isExpanded ? (
            <Minimize2 className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Maximize2 className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </div>
      
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-5 p-1 bg-muted rounded-md">
        {[
          { id: 'basic', label: 'Basic', icon: Users },
          { id: 'ai', label: 'AI', icon: Settings },
          { id: 'advanced', label: 'Variables', icon: Target },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as 'basic' | 'ai' | 'advanced')}
            className={cn(
              "flex-1 flex items-center justify-center space-x-1.5 py-2 px-2 rounded text-sm font-medium transition-all duration-150",
              activeTab === tab.id 
                ? "bg-background text-foreground shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <tab.icon className="h-3.5 w-3.5" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
      
      <div className="space-y-5">
        {/* Basic Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-5 animate-in fade-in duration-200">
      {/* Sample Size */}
            <div className="space-y-2">
        <label className="flex items-center space-x-2 text-sm font-medium">
          <Users className="h-4 w-4 text-muted-foreground" />
                <span>Respondents</span>
        </label>
        <input
          type="number"
          min="1"
          max="500"
          value={sampleSize}
          onChange={(e) => setSampleSize(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                className="w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
                disabled={isLoading}
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">1 - 500</p>
                <div className="flex space-x-1.5">
                  {[10, 30, 50, 100].map(size => (
                    <button
                      key={size}
                      onClick={() => setSampleSize(size)}
                      disabled={isLoading}
                      className={cn(
                        "px-2 py-0.5 rounded text-xs font-medium transition-colors",
                        sampleSize === size 
                          ? "bg-foreground text-background" 
                          : "bg-muted text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Target Variable Selector - Same UI as Features */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 text-sm font-medium">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span>Target Variable</span>
                </label>
                <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                  {targetVariable ? '1 selected' : '0 selected'}
                </span>
              </div>
              
              {/* Search and Filter */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <Search className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <input
                    type="text"
                    value={targetVarSearch}
                    onChange={(e) => setTargetVarSearch(e.target.value)}
                    placeholder="Search target variables..."
                    className="w-full pl-7 pr-2 py-1.5 text-xs border border-input bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <select
                  value={targetVarCategory}
                  onChange={(e) => setTargetVarCategory(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-input bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="all">All Categories</option>
                  {Array.from(new Set(targetVariables.map(tv => tv.category))).sort().map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              {/* Grouped by Category */}
              <div className="border rounded-md overflow-y-auto max-h-64">
                {Object.entries(
                  targetVariables
                    .filter(tv => {
                      const matchesSearch = fuzzyMatch(
                        targetVarSearch,
                        tv.name,
                        tv.label,
                        tv.category,
                        tv.description
                      );
                      const matchesCategory = targetVarCategory === 'all' || tv.category === targetVarCategory;
                      return matchesSearch && matchesCategory;
                    })
                    .reduce((acc, tv) => {
                      if (!acc[tv.category]) acc[tv.category] = [];
                      acc[tv.category].push(tv);
                      return acc;
                    }, {} as Record<string, typeof targetVariables>)
                ).map(([category, vars]) => (
                  <div key={category} className="border-b last:border-b-0">
                    <div className="p-2 bg-muted/30">
                      <span className="text-xs font-medium">
                        {category} ({vars.length})
                      </span>
                    </div>
                    <div className="p-1.5 space-y-0.5">
                      {vars.map(tv => {
                        const isSelected = targetVariable === tv.name;
                        return (
                          <label
                            key={tv.name}
                            className={cn(
                              "flex items-start space-x-2.5 p-2 rounded cursor-pointer transition-colors",
                              isSelected 
                                ? "bg-foreground/10 border border-foreground/20" 
                                : "hover:bg-muted/50 border border-transparent"
                            )}
                          >
                            <input
                              type="radio"
                              name="targetVariable"
                              checked={isSelected}
                              onChange={() => setTargetVariable(tv.name)}
                              className="mt-0.5"
                              disabled={isLoading}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-1.5 flex-wrap gap-0.5">
                                <span className="font-medium text-sm">{tv.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  ({tv.valid_count} valid)
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {tv.label}
                              </p>
                              {tv.description && (
                                <p className="text-[10px] text-muted-foreground mt-0.5 italic">
                                  {tv.description}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Prompt */}
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm font-medium">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <span>Prompt</span>
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder={defaultPrompt}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-ring resize-vertical transition-colors"
                disabled={isLoading}
              />
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  Leave empty for default
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowPromptPreview(true)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    disabled={isLoading}
                  >
                    <Eye className="h-3 w-3" />
                    Preview
                  </button>
                  <button
                    onClick={() => setCustomPrompt(defaultPrompt)}
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    disabled={isLoading}
                  >
                    Use default
                  </button>
                </div>
              </div>
            </div>

            {/* Parallel Processing */}
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm font-medium">
                <Zap className="h-4 w-4 text-muted-foreground" />
                <span>Parallel Requests</span>
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={maxConcurrency}
                  onChange={(e) => setMaxConcurrency(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-foreground"
          disabled={isLoading}
        />
                <span className="w-8 text-sm font-medium text-center">{maxConcurrency}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Higher values = faster but may hit rate limits
              </p>
            </div>

            {/* Streaming Toggle */}
            <div className="flex items-center justify-between p-3 border rounded-md">
              <div className="flex items-center space-x-2">
                <Radio className="h-4 w-4 text-muted-foreground" />
                <div>
                  <span className="text-sm font-medium">Live Streaming</span>
                  <p className="text-xs text-muted-foreground">See results in real-time</p>
                </div>
      </div>

              <button
                onClick={() => setEnableStreaming(!enableStreaming)}
                disabled={isLoading}
                className={cn(
                  "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                  enableStreaming ? "bg-foreground" : "bg-muted",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
              >
                <span
                  className={cn(
                    "inline-block h-3.5 w-3.5 transform rounded-full bg-background transition-transform duration-200",
                    enableStreaming ? "translate-x-5" : "translate-x-1"
                  )}
                />
              </button>
            </div>
          </div>
        )}

        {/* AI Configuration Tab */}
        {activeTab === 'ai' && (
          <div className="space-y-5 animate-in fade-in duration-200">
            <ModelSelector
              reasoningEffort={reasoningEffort}
              onReasoningEffortChange={setReasoningEffort}
              isLoading={isLoading}
            />
            
            <FewShotManager
              examples={fewShotExamples}
              onExamplesChange={setFewShotExamples}
              enableChainOfThought={enableChainOfThought}
              onChainOfThoughtChange={setEnableChainOfThought}
              isLoading={isLoading}
            />
          </div>
        )}

        {/* Variables Tab */}
        {activeTab === 'advanced' && (
          <div className="space-y-3 animate-in fade-in duration-200">
            <div className="flex items-center justify-between">
        <label className="flex items-center space-x-2 text-sm font-medium">
          <Target className="h-4 w-4 text-muted-foreground" />
                <span>Variables</span>
        </label>
              <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                {selectedVariables.length} selected
              </span>
            </div>
        
        {/* Search Bar */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <input
            type="text"
                placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-9 pr-9 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
            disabled={isLoading || loadingVariables}
          />
          {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
          )}
        </div>
        
            {/* Controls */}
        <div className="flex flex-wrap gap-2 items-center">
              <div className="flex items-center space-x-1.5">
            <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'category' | 'importance')}
                  className="text-xs border border-input bg-background rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring"
              disabled={isLoading || loadingVariables}
            >
                  <option value="category">Category</option>
                  <option value="importance">Importance</option>
                  <option value="name">Name</option>
            </select>
          </div>
          
              <div className="flex items-center space-x-1.5">
            <Filter className="h-3 w-3 text-muted-foreground" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
                  className="text-xs border border-input bg-background rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring"
              disabled={isLoading || loadingVariables}
            >
                  <option value="all">All</option>
              {availableCategories.sort().map(category => (
                <option key={category} value={category}>
                  {categoryLabels[category] || category}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-1.5 ml-auto">
            <button
              onClick={() => {
                // Select recommended/important variables
                const important = variables.filter(v => getImportanceScore(v) >= 10).map(v => v.name);
                setSelectedVariables(important);
              }}
              className="text-xs text-muted-foreground hover:text-foreground px-2 py-0.5 rounded bg-muted"
              disabled={isLoading}
            >
              Recommended
            </button>
            <button
              onClick={() => setSelectedVariables(variables.map(v => v.name))}
              className="text-xs text-muted-foreground hover:text-foreground px-2 py-0.5 rounded bg-muted"
              disabled={isLoading}
            >
              All
            </button>
            {selectedVariables.length > 0 && (
              <button
                onClick={() => setSelectedVariables([])}
                className="text-xs text-muted-foreground hover:text-foreground px-2 py-0.5 rounded"
                disabled={isLoading}
              >
                Clear
            </button>
          )}
          </div>
        </div>
        
        {loadingVariables ? (
          <div className="text-center py-6">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-foreground border-t-transparent mx-auto"></div>
                <p className="text-xs text-muted-foreground mt-2">Loading...</p>
          </div>
        ) : (
          <div className={cn(
                "border rounded-md overflow-y-auto",
                isExpanded ? "max-h-[50vh]" : "max-h-56"
          )}>
            {Object.entries(variablesByCategory).map(([category, categoryVars]) => {
              const selectedInCategory = categoryVars.filter(v => selectedVariables.includes(v.name)).length;
              return (
                  <div key={category} className="border-b last:border-b-0">
                <div
                      className="flex items-center justify-between p-2.5 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleCategory(category)}
                >
                  <span className="font-medium text-sm">
                    {categoryLabels[category]} 
                    <span className="text-muted-foreground ml-1">
                      ({selectedInCategory}/{categoryVars.length})
                    </span>
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAllInCategory(category);
                      }}
                          className="text-xs text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded bg-muted"
                    >
                      All
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deselectAllInCategory(category);
                      }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      None
                    </button>
                    {expandedCategories.has(category) ? (
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                </div>
                
                {expandedCategories.has(category) && (
                      <div className="p-1.5 space-y-0.5">
                    {categoryVars.map((variable) => {
                      const importance = getImportanceScore(variable);
                      const isHighImportance = importance >= 10;
                      const isSelected = selectedVariables.includes(variable.name);
                      
                      return (
                        <label key={variable.name} className={cn(
                              "flex items-start space-x-2.5 p-2 rounded cursor-pointer transition-colors",
                              isSelected 
                                ? "bg-foreground/5 border border-foreground/10" 
                                : "hover:bg-muted/50 border border-transparent"
                        )}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleVariable(variable.name)}
                                className="mt-0.5 rounded border-input"
                            disabled={isLoading}
                          />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-1.5 flex-wrap gap-0.5">
                                  <span className="font-medium text-sm">{variable.name}</span>
                              {isHighImportance && (
                                    <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
                                      Key
                                </span>
                              )}
                            </div>
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                  {variable.label}
                                </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
        )}

      {/* Run Simulation Button */}
      <button
        onClick={onRunSimulation}
        disabled={isLoading}
          className={cn(
            "w-full font-medium py-3 px-4 rounded-md transition-all duration-200 flex items-center justify-center space-x-2",
            isLoading 
              ? "bg-muted text-muted-foreground cursor-not-allowed" 
              : "bg-foreground text-background hover:bg-foreground/90"
          )}
      >
        {isLoading ? (
          <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-muted-foreground border-t-transparent"></div>
              <span>Processing...</span>
          </>
        ) : (
            <>
              <Play className="h-4 w-4" />
              <span>Run Simulation</span>
              {enableStreaming && (
                <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-background/20">
                  Live
                </span>
              )}
            </>
        )}
      </button>
      </div>

      {/* Prompt Preview Modal */}
      {showPromptPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center space-x-2">
                <Eye className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-lg font-semibold">Prompt Preview</h2>
              </div>
              <button
                onClick={() => setShowPromptPreview(false)}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* System Message */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase">System Message</span>
                </div>
                <div className="bg-muted/50 p-3 rounded text-sm font-mono whitespace-pre-wrap border">
                  {enableChainOfThought ? (
                    `Sei un giovane cittadino italiano. Rispondi sempre in italiano. Basati esclusivamente sul tuo profilo personale per formare le tue opinioni. Sii autentico e coerente con i tuoi valori e atteggiamenti.

Prima di rispondere, ragiona passo per passo considerando:
1. Il tuo background demografico e come influenza le tue opinioni
2. Le tue esperienze politiche e il tuo orientamento
3. I tuoi valori e atteggiamenti verso le istituzioni
4. Come questi fattori si combinano per formare la tua opinione finale

Dopo il ragionamento, concludi SEMPRE con 'La mia risposta finale è: si' oppure 'La mia risposta finale è: no'`
                  ) : (
                    `Sei un giovane cittadino italiano. Rispondi sempre in italiano. Basati esclusivamente sul tuo profilo personale per formare le tue opinioni. Sii autentico e coerente con i tuoi valori e atteggiamenti.`
                  )}
                </div>
              </div>

              {/* Few-Shot Examples */}
              {fewShotExamples.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase">Few-Shot Examples</span>
                    <span className="text-xs text-muted-foreground">({fewShotExamples.length})</span>
                  </div>
                  {fewShotExamples.map((example, idx) => (
                    <div key={idx} className="space-y-2 border-l-2 border-muted pl-3">
                      <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded text-sm">
                        <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">User:</div>
                        <div className="whitespace-pre-wrap">{`Ecco il profilo di un giovane italiano:\n\n${example.profile_summary}\n\n${customPrompt || defaultPrompt.split('\n')[0]}`}</div>
                      </div>
                      <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded text-sm">
                        <div className="text-xs font-medium text-green-700 dark:text-green-300 mb-1">Assistant:</div>
                        <div className="whitespace-pre-wrap">
                          {enableChainOfThought && example.reasoning
                            ? `${example.reasoning}\n\nLa mia risposta finale è: ${example.answer}`
                            : `Basandomi sul mio profilo, la mia risposta è: ${example.answer}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* User Message - Editable */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground uppercase">User Message</span>
                  <span className="text-xs text-muted-foreground">Editable</span>
                </div>
                <div className="bg-muted/50 p-3 rounded text-sm border space-y-2">
                  <div className="font-mono whitespace-pre-wrap text-xs text-muted-foreground mb-2">
                    {`Ecco il tuo profilo personale:

[Persona Profile - ${selectedVariables.length} variabili selezionate]
${selectedVariables.length > 0 
  ? selectedVariables.slice(0, 5).map(v => `- ${v}`).join('\n') + (selectedVariables.length > 5 ? `\n... e altre ${selectedVariables.length - 5} variabili` : '')
  : 'Nessuna variabile selezionata - verranno usate tutte le variabili disponibili'}

Domanda:`}
                  </div>
                  <textarea
                    value={customPrompt || defaultPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-ring resize-vertical font-mono"
                    rows={6}
                    placeholder={defaultPrompt}
                  />
                  <div className="font-mono whitespace-pre-wrap text-xs text-muted-foreground mt-2">
                    {`

Istruzioni:
${enableChainOfThought 
  ? `• Ragiona passo per passo su come il tuo profilo influenza la tua risposta
• Considera tutti gli aspetti del tuo background
• Concludi con 'La mia risposta finale è: si' oppure 'La mia risposta finale è: no'
• Non essere neutrale - prendi una posizione chiara`
  : `• Rifletti su come il tuo background, i tuoi valori e le tue esperienze influenzano la tua risposta
• Considera sia gli aspetti positivi che negativi della questione
• Fornisci una risposta onesta basata sul tuo profilo
• La tua risposta deve terminare con una sola parola: 'si' o 'no'
• Non essere neutrale - prendi una posizione chiara`}`}
                  </div>
                </div>
              </div>

              {/* Configuration Summary */}
              <div className="bg-muted/30 p-3 rounded text-xs space-y-1">
                <div className="font-medium mb-2">Configuration:</div>
                <div>• Model: {reasoningEffort === 'none' ? 'GPT-5 Nano' : reasoningEffort === 'low' ? 'GPT-5 Nano (Low)' : reasoningEffort === 'medium' ? 'GPT-5 (Medium)' : 'GPT-5 (High)'}</div>
                <div>• Reasoning Effort: {reasoningEffort}</div>
                <div>• Chain-of-Thought: {enableChainOfThought ? 'Enabled' : 'Disabled'}</div>
                <div>• Few-Shot Examples: {fewShotExamples.length}</div>
                <div>• Selected Variables: {selectedVariables.length}</div>
                <div>• Target Variable: {targetVariable}</div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setShowPromptPreview(false)}
                className="px-4 py-2 text-sm bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParameterControls;
